import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import UISection from '../section/UISection';
import UIIcon from '../icon/UIIcon';
import { getIconNamePropType } from '../utils/propTypes/iconName';
import { isIE11 } from '../utils/BrowserTest';
import { propTypeForSizes, toShorthandSize } from '../utils/propTypes/tshirtSize';
import { CALYPSO, CALYPSO_LIGHT, CANDY_APPLE_DARK, CANDY_APPLE_LIGHT, EERIE, GYPSUM, OBSIDIAN, OZ, OZ_DARK, OZ_LIGHT, SLINKY } from 'HubStyleTokens/colors';
import { DROP_SMALL_HEIGHT, DROP_LARGE_HEIGHT } from 'HubStyleTokens/sizes';
import { BASE_FONT_COLOR, BASE_LINK_COLOR } from 'HubStyleTokens/theme';
const CONTENT_PADDING_X = 20;
const STYLES_FOR_USE = {
  hover: {
    backgroundColor: CALYPSO_LIGHT,
    border: `1px dashed ${CALYPSO}`,
    iconColor: BASE_LINK_COLOR
  },
  processing: {
    border: `1px solid ${GYPSUM}`,
    iconColor: EERIE
  },
  selected: {
    border: `1px solid ${GYPSUM}`,
    color: OBSIDIAN,
    iconColor: OBSIDIAN
  },
  error: {
    backgroundColor: CANDY_APPLE_LIGHT,
    border: `1px dashed ${CANDY_APPLE_DARK}`,
    color: BASE_FONT_COLOR,
    iconColor: CANDY_APPLE_DARK
  },
  success: {
    backgroundColor: OZ_LIGHT,
    border: `1px dashed ${OZ_DARK}`,
    color: BASE_FONT_COLOR,
    iconColor: OZ
  }
};
const Outer = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, ["variantStyles"]);

  return /*#__PURE__*/_jsx(UISection, Object.assign({}, rest));
}).withConfig({
  displayName: "UIDropZone__Outer",
  componentId: "sc-1ux5qg0-0"
})(["display:flex;flex-direction:column;background-color:", ";border:", ";border-radius:4px;color:", ";position:relative;"], ({
  variantStyles
}) => variantStyles.backgroundColor || GYPSUM, ({
  variantStyles
}) => variantStyles.border || `1px dashed ${SLINKY}`, ({
  variantStyles
}) => variantStyles.color || EERIE); // Fix for #6763: Under IE11, images don't respect `max-width: 100%` in this flex container.

const imgWidthMixin = css(["img{max-width:", "px;}"], ({
  contentWidth
}) => contentWidth && contentWidth - CONTENT_PADDING_X * 2);
const Content = styled.div.withConfig({
  displayName: "UIDropZone__Content",
  componentId: "sc-1ux5qg0-1"
})(["display:flex;min-height:", ";flex-direction:", ";align-items:center;justify-content:center;text-align:center;padding:24px ", "px;", ""], ({
  size
}) => size === 'sm' ? DROP_SMALL_HEIGHT : DROP_LARGE_HEIGHT, ({
  size
}) => size === 'sm' ? 'row' : 'column', CONTENT_PADDING_X, ({
  contentWidth
}) => contentWidth && imgWidthMixin);
const Icon = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, ["dropSize"]);

  return /*#__PURE__*/_jsx(UIIcon, Object.assign({}, rest));
}).withConfig({
  displayName: "UIDropZone__Icon",
  componentId: "sc-1ux5qg0-2"
})(["padding-right:", ";padding-bottom:", ";"], ({
  dropSize
}) => dropSize === 'sm' && '12px', ({
  dropSize
}) => dropSize === 'lg' && '16px');
const Body = styled.div.withConfig({
  displayName: "UIDropZone__Body",
  componentId: "sc-1ux5qg0-3"
})(["display:flex;flex-direction:column;"]);
const emptyObject = {};

class UIDropZone extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      contentWidth: null
    };
    this._contentEl = {
      offsetWidth: 0
    };

    this.computeWidth = () => {
      if (!isIE11()) return;
      const contentWidth = this._contentEl.offsetWidth;

      if (contentWidth && contentWidth !== this.state.contentWidth) {
        this.setState({
          contentWidth
        });
      }
    };
  }

  componentDidMount() {
    this.computeWidth();
  }

  componentDidUpdate(prevProps, prevState) {
    // ⚠️ This condition prevents infinite loops! ⚠️
    if (prevState.contentWidth === this.state.contentWidth) {
      this.computeWidth();
    }
  }

  render() {
    const _this$props = this.props,
          {
      children,
      className,
      contentClassName,
      iconName,
      iconSize,
      size,
      use
    } = _this$props,
          rest = _objectWithoutPropertiesLoose(_this$props, ["children", "className", "contentClassName", "iconName", "iconSize", "size", "use"]);

    const {
      contentWidth
    } = this.state;
    const shorthandSize = toShorthandSize(size);
    const computedClassName = classNames('private-drop-zone', className, shorthandSize === 'lg' && 'private-drop-zone--large');
    const variantStyles = STYLES_FOR_USE[use] || emptyObject;
    return /*#__PURE__*/_jsx(Outer, Object.assign({}, rest, {
      className: computedClassName,
      variantStyles: variantStyles,
      children: /*#__PURE__*/_jsxs(Content, {
        className: classNames('private-drop-zone__content', contentClassName),
        ref: ref => {
          this._contentEl = ref;
        },
        contentWidth: contentWidth,
        size: shorthandSize,
        children: [iconName ? /*#__PURE__*/_jsx(Icon, {
          name: iconName,
          dropSize: shorthandSize,
          size: iconSize || (shorthandSize === 'sm' ? 24 : 48),
          color: variantStyles.iconColor
        }) : null, /*#__PURE__*/_jsx(Body, {
          children: children
        })]
      })
    }));
  }

}

UIDropZone.displayName = 'UIDropZone';
UIDropZone.propTypes = {
  children: PropTypes.node,
  contentClassName: PropTypes.string,
  iconName: getIconNamePropType(),
  iconSize: UIIcon.propTypes.size,
  size: propTypeForSizes(['sm', 'lg']).isRequired,
  use: PropTypes.oneOf(['default', 'hover', 'processing', 'selected', 'error', 'success']).isRequired
};
UIDropZone.defaultProps = {
  size: 'sm',
  use: 'default'
};
export default UIDropZone;